import React from "react"
import PropTypes from "prop-types"
import Styled from "styled-components"
import Img from "gatsby-image"
import {getFluidGatsbyImage} from "gatsby-storyblok-image"
import Icon from "../Icon"
import {H1} from "@uprise/headings/H1"
import {H4} from "@uprise/headings/H4"
import {P} from "@uprise/text"
import {spacing} from "@uprise/spacing"
import {navigateTo, VideoPlayerContext} from "@utils"
import {Button} from "@uprise/button"

const HeroBannerWrapper = Styled.div`
	display: flex;
	flex-direction: ${props => {
		if (props.splitScreen) {
			return props.flipImageCopy ? "row-reverse" : "row"
		} else {
			return props.flipImageCopy ? "column-reverse" : "column"
		}
	}};
	padding-top: 50px;
	align-items: center;
	
	@media (max-width: 1024px) {
		padding-top: 0;
		padding-bottom: 30px;
		flex-direction: column-reverse;
	}

	@media (max-width: 475px) {
		flex-direction: column-reverse;
	}
`

const ContentWrap = Styled.div`
	width: 50%;

	@media (max-width: 1024px) {
		width: 100%;
	}

	@media (max-width: 475px) {
		width: 100%;
	}
`

const Content1 = Styled.div`
	display: flex;
	flex-direction: column;
	text-align: ${props => (props.splitScreen ? "left" : "center")};
	align-items: ${props => (props.splitScreen ? "flex-start" : "center")};;
 	max-width: ${props => (props.copyWidth ? `${props.copyWidth}px` : "100%")};
	min-height: ${props => (props.onlyCTA ? "50px" : "200px")};
	top: ${props => (props.copyMarginTop ? `${props.copyMarginTop}px` : "auto")}
	position: relative; 

	@media (max-width: 1024px) {
		margin-top: 0;
		top: ${spacing.s10};
		text-align: center;
		max-width: 100%;
		align-items: center;
	}

	@media (max-width: 475px) {
		top: ${spacing.s10};
		margin-top: 0;
		text-align: center;
		max-width: 100%;
		align-items: center;
	}

`

const Content2 = Styled.div`
	margin: ${props => (props.splitScreen ? "0px auto" : "50px auto 20px")};
	display: flex;
	flex-direction: row;
	justify-content: ${props => props.justifyImage};
`

const HeroImageWrap = Styled.div`
	align-self: ${props => props.justifyImage};
	width: ${props => (props.imageWidth ? `${props.imageWidth}px` : "100%")};
	height: ${props => (props.imageHeight ? `${props.imageHeight}px` : "100%")};

	@media (max-width: 1024px) {
		align-self: center;
		width: 75%;
		margin: -50px auto 0 auto;
		min-height: 0;
		height: auto;
	}

	@media (max-width : 768px){
		width: 100%;
	}

	@media (max-width: 475px) {
		align-self: center;
		width: 100%;
		margin: 0 auto;
		min-height: 0;
		height: auto;
	}

`

const HeroCtaWrap = Styled.div`
	margin-top: ${props => (props.onlyCTA ? "0px" : "40px")};
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: ${props => props.alignButtons};
	width: 100%;	

	@media (max-width: 1024px) {
	     justify-content: center;
	     margin-top: ${spacing.s7};
     	}
    @media (max-width : 768px){
		flex-direction: column;
	}
	@media (max-width: 475px) {
		justify-content: center;
		flex-direction: column;
	}
`

const ButtonWrap = Styled.div`
	display: flex;
	flex-direction: row;
	width: 100%;
	margin-right: ${props => (props.splitScreen ? `${spacing.s6}` : `0px`)};
	margin-bottom: ${props => (props.splitScreen ? `${spacing.s6}` : `0px`)};
	justify-content: ${props => props.align};

	@media(max-width : 768px){
	  width : 100%
  	}
`

const Point = Styled.div`
     display: flex;
     flex-direction: row;
`

const ButtonComponent = Styled(Button)`

  @media(max-width : 768px){
	  width : 100%;
	  margin-top : ${props => (props.className === "mt-sm" ? "12px" : "")}
  }
`

function HeroBanner({
	flipImageCopy,
	copyMarginTop,
	copyWidth,
	title,
	subTitle,
	body,
	action,
	image,
	splitScreen,
	media,
	points,
	titleWidth,
	whiteText,
	imageHeight,
	imageWidth,
	justifyImage,
	alignButtons
}) {
	const noMedia = !image && !media
	const onlyCTA = noMedia && !title && !subTitle && !body && !points
	const {togglePlayVideo, setURL} = React.useContext(VideoPlayerContext)

	const fluidProps = getFluidGatsbyImage(media, {})

	return (
		<HeroBannerWrapper splitScreen={splitScreen} flipImageCopy={flipImageCopy}>
			<ContentWrap>
				<Content1
					splitScreen={splitScreen}
					copyMarginTop={copyMarginTop}
					copyWidth={copyWidth}
					noMedia={noMedia}
					onlyCTA={onlyCTA}
					titleWidth={titleWidth}
					whiteText={whiteText}>
					{title && (
						<H1 className='m-b-4' width={titleWidth} color={whiteText ? "#fff" : null}>
							{title}
						</H1>
					)}

					{subTitle && (
						<H4 className='m-b-5' weight='normal' color={whiteText ? "#fff" : null}>
							{subTitle}
						</H4>
					)}

					{body && <P color={whiteText ? "#fff" : null}>{body}</P>}

					{points &&
						points.split(",").map((point, index) => {
							return (
								<Point key={index}>
									<Icon className='m-t-2' name='tick' />
									<P className='m-l-3'>{point}</P>
								</Point>
							)
						})}

					{action && action.length > 0 && (
						<HeroCtaWrap alignButtons={alignButtons}>
							{action.map((item, index) => {
								// fix old prop values from legacy component
								if (item.variant === "PRIMARY") {
									item.variant = "primary"
								} else if (item.variant === "OUTLINE") {
									item.variant = "secondary"
								}

								if (
									item.size !== "large" ||
									item.size !== "medium" ||
									item.size !== "small" ||
									item.size !== "tiny"
								) {
									item.size = "large"
								}

								return (
									<ButtonWrap key={index} splitScreen={splitScreen} align={item.alignHorizontal}>
										<ButtonComponent
											key={item._uid}
											className={item.className}
											onClick={() =>
												navigateTo(item.link, {
													fn: () => {
														setURL("https://player.vimeo.com/video/219316897")
														togglePlayVideo()
													}
												})
											}
											title={item.label || item.title}
											paddingLeft={item.paddingLeft}
											paddingRight={item.paddingRight}
											size={item.size}
											fullWidth={item.fullWidth}
											variant={item?.variant}
											transparent={item.white}
										/>
									</ButtonWrap>
								)
							})}
						</HeroCtaWrap>
					)}
				</Content1>
			</ContentWrap>
			<ContentWrap>
				<Content2 justifyImage={justifyImage} splitScreen={splitScreen}>
					{fluidProps && (
						<HeroImageWrap
							splitScreen={splitScreen}
							imageWidth={imageWidth}
							// imageHeight={imageHeight}
							justifyImage={justifyImage}>
							{fluidProps && <Img fluid={fluidProps} alt={"hero"} loading='eager' />}
						</HeroImageWrap>
					)}
				</Content2>
			</ContentWrap>
		</HeroBannerWrapper>
	)
}

export default HeroBanner

HeroBanner.propTypes = {
	title: PropTypes.string.isRequired,
	points: PropTypes.string
}
HeroBanner.defaultProps = {
	title: "Uprise",
	whiteText: false
}
